import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UniversityManagerLayout from "../../components/layout/UniversityManagerLayout";
import UMLogRecordsList from "../../features/university-manager/UMLogRecordsList";
import AppButton from "../../components/ui/AppButton";
import PermissionGuard from "../../components/common/PermissionGuard";
import { useTenant } from "../../context/TenantContext";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";
import umService from "../../services/university-admin/umService";
import { isMockDataUM } from "../../config/featureFlags";

const UMLogRecords = () => {
  const { t, i18n } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");
  const { can } = useUniAdminAuth();
  const { userRole, currentTenantId } = useTenant();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(10);
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    actionType: "",
    actorRole: "",
    targetType: "",
    search: "",
  });

  const payload = useMemo(
    () => ({
      userRole,
      currentTenantId,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      actionType: filters.actionType || undefined,
      actorRole: filters.actorRole || undefined,
      targetType: filters.targetType || undefined,
      search: filters.search || undefined,
      page: currentPage,
      size: pageSize,
    }),
    [userRole, currentTenantId, filters, currentPage, pageSize]
  );

  const fetchLogs = useCallback(async () => {
    // Real API requires from and to — skip fetch until both are set
    if (!isMockDataUM && (!filters.dateFrom || !filters.dateTo)) {
      setLogs([]);
      setTotalPages(0);
      setTotalElements(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    const response = await umService.getActivityLogs(payload);
    if (response.success) {
      setLogs(response.data.content);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
    }
    setLoading(false);
  }, [payload, filters.dateFrom, filters.dateTo]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(0);
  };

  const handleClearFilters = () => {
    setFilters({
      dateFrom: "",
      dateTo: "",
      actionType: "",
      actorRole: "",
      targetType: "",
      search: "",
    });
    setCurrentPage(0);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const startPage = Math.max(0, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);
    for (let i = startPage; i <= endPage; i += 1) {
      pages.push(
        <AppButton key={i} variant={i === currentPage ? "primary" : "outline"} className="btn-sm mx-1" onClick={() => setCurrentPage(i)}>
          {i + 1}
        </AppButton>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          {tCommon("pagination.showingEntries", {
            start: currentPage * pageSize + 1,
            end: Math.min((currentPage + 1) * pageSize, totalElements),
            total: totalElements,
          })}
        </div>
        <div className="d-flex align-items-center">
          <AppButton variant="outline" className="btn-sm me-2" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0}>
            {tCommon("buttons.previous")}
          </AppButton>
          {pages}
          <AppButton
            variant="outline"
            className="btn-sm ms-2"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
          >
            {tCommon("buttons.next")}
          </AppButton>
        </div>
      </div>
    );
  };

  return (
    <UniversityManagerLayout>
      <PermissionGuard allow={can(PERMISSIONS.auditLog.list)}>
      <div className="um-log-records-page">
        <div className="d-flex justify-content-between align-items-center mb-4 gap-3 flex-wrap">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">
              <i className="fas fa-clipboard-list me-2 text-secondary"></i>
              {t("logs.title")}
            </h1>
            <p className="text-muted mb-0">{t("logs.subtitle")}</p>
          </div>
        </div>

        <UMLogRecordsList
          logs={logs}
          loading={loading}
          totalElements={totalElements}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          i18nLanguage={i18n.language}
        />

        {renderPagination()}
      </div>
      </PermissionGuard>
    </UniversityManagerLayout>
  );
};

export default UMLogRecords;
